import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { DealTypes } from 'api';
import { colors } from 'theme/constants';
import { formatCurrency } from 'helpers/formatters';
import { getAttributionModelField, isNotEmptyValue } from 'helpers/common';
import { MetricCard, MetricCardProps } from 'components';
import { AttributionModelEnum } from '../../../../../types/common';

interface Props {
  inline?: boolean;
  deal: DealTypes.ItemDataExtended;
  attributionModel: AttributionModelEnum;
}

export type DealInfoItems = ({
  key: keyof DealTypes.ItemData;
} & Omit<MetricCardProps, 'title'>)[];

const PerformanceTab = (props: Props): JSX.Element | null => {
  const { deal, inline, attributionModel } = props;

  const primaryMetricsInfo: DealInfoItems = useMemo(
    () => [
      {
        key: 'budget_actual',
        subtitle: 'Budget actual',
        color: colors.accent,
        showTrend: isNotEmptyValue(deal.budget_actual),
      },
      {
        key: getAttributionModelField(attributionModel, 'roi_actual'),
        subtitle: 'ROI actual',
        color: colors.purple,
        showTrend: isNotEmptyValue(deal.roi_actual),
        subtitleCaption: `(${attributionModel})`,
      },
      {
        key: getAttributionModelField(attributionModel, 'revenue_actual'),
        subtitle: 'Revenue actual',
        showTrend: isNotEmptyValue(deal.revenue_actual),
        color: colors.darkGreen,
        subtitleCaption: `(${attributionModel})`,
      },
      {
        key: getAttributionModelField(attributionModel, 'gross_profit_actual'),
        subtitle: 'Gross profit actual',
        showTrend: isNotEmptyValue(deal.gross_profit_actual),
        color: colors.darkBlue,
        subtitleCaption: `(${attributionModel})`,
      },
    ],
    [
      attributionModel,
      deal.budget_actual,
      deal.gross_profit_actual,
      deal.revenue_actual,
      deal.roi_actual,
    ],
  );

  return (
    <Box>
      <Grid container spacing={3}>
        {primaryMetricsInfo.map((item) => (
          <Grid item xs={12} sm={6} md={inline ? 6 : 3} key={item.key}>
            <MetricCard
              title={
                (item.key.includes('roi')
                  ? deal[item.key]
                  : formatCurrency(deal[item.key], 0)) ?? '-'
              }
              subtitle={item.subtitle}
              subtitleCaption={item.subtitleCaption}
              color={item.color}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PerformanceTab;
